import { ServiceEmailSolicitud } from "../../../models/ServiceEmailSolicitudModel";

export type MonitorMode = "servicio" | "conductor";

export type OpsStatus =
  | "sin_servicio"
  | "parcial"
  | "sin_conductor"
  | "listo"
  | "idle";

export const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};

export const formatServiceDate = (raw?: string | null) => {
  if (!raw) return "—";
  const [year, month, day] = raw.slice(0, 10).split("-");
  if (!year || !month || !day) return raw;
  return `${day}/${month}/${year.slice(-2)}`;
};

export const formatTime = (raw?: string | null) => {
  if (!raw) return "—";
  const match = String(raw).match(/^(\d{1,2}):(\d{2})/);
  return match ? `${match[1].padStart(2, "0")}:${match[2]}` : raw;
};

export const formatClock = (date?: Date | null) => {
  if (!date) return "—";
  return date.toLocaleTimeString("es-CL", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

export const formatRelative = (iso?: string | null) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return "ahora";
  if (mins < 60) return `hace ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `hace ${hours} h`;
  return date.toLocaleString("es-CL", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const getActiveLinks = (item: ServiceEmailSolicitud) =>
  (item.service_links ?? []).filter((link) => link.service_id || link.service);

export const getRemaining = (item: ServiceEmailSolicitud) =>
  item.remaining_passenger_count ??
  Math.max(0, (item.passenger_count || 0) - (item.assigned_passenger_count ?? 0));

export const getAssigned = (item: ServiceEmailSolicitud) =>
  item.assigned_passenger_count ?? 0;

export const hasMissingDriver = (item: ServiceEmailSolicitud) => {
  const links = getActiveLinks(item);
  if (!links.length) return false;
  return links.some((link) => !String(link.driver_name || "").trim());
};

export const resolveOpsStatus = (item: ServiceEmailSolicitud): OpsStatus => {
  const remaining = getRemaining(item);
  const assigned = getAssigned(item);
  const links = getActiveLinks(item);

  if (remaining <= 0 && assigned <= 0 && !links.length) return "idle";
  if (!links.length || (remaining > 0 && assigned <= 0)) return "sin_servicio";
  if (remaining > 0) return "parcial";
  if (hasMissingDriver(item)) return "sin_conductor";
  return "listo";
};

export const OPS_STATUS_LABEL: Record<OpsStatus, string> = {
  sin_servicio: "Sin servicio",
  parcial: "Parcial",
  sin_conductor: "Sin conductor",
  listo: "Listo",
  idle: "Sin pax",
};

export type MonitorStats = {
  total: number;
  sinServicio: number;
  parcial: number;
  sinConductor: number;
  listo: number;
  /** Alias del total del listado filtrado (periodo activo). */
  scopeCount: number;
  scopeSinServicio: number;
  scopeSinConductor: number;
};

export const formatDayLabel = (iso?: string | null) => {
  const day = (iso || "").trim();
  if (!day) return "Sin fecha";
  if (day === todayIso()) return "Hoy";
  return formatServiceDate(day);
};

/** @deprecated usar formatDayLabel — el monitor opera por un solo día */
export const formatRangeLabel = (dateFrom?: string | null, dateTo?: string | null) => {
  const from = (dateFrom || "").trim();
  const to = (dateTo || "").trim() || from;
  if (!from && !to) return "Sin fecha";
  return formatDayLabel(from || to);
};

export const computeMonitorStats = (data: ServiceEmailSolicitud[]): MonitorStats => {
  const stats: MonitorStats = {
    total: data.length,
    sinServicio: 0,
    parcial: 0,
    sinConductor: 0,
    listo: 0,
    scopeCount: data.length,
    scopeSinServicio: 0,
    scopeSinConductor: 0,
  };

  for (const item of data) {
    const status = resolveOpsStatus(item);

    if (status === "sin_servicio") {
      stats.sinServicio += 1;
      stats.scopeSinServicio += 1;
    } else if (status === "parcial") {
      stats.parcial += 1;
      stats.scopeSinServicio += 1;
    } else if (status === "sin_conductor") {
      stats.sinConductor += 1;
      stats.scopeSinConductor += 1;
    } else if (status === "listo") {
      stats.listo += 1;
    }
  }

  return stats;
};

export type MonitorAlert = {
  id: string;
  tone: "danger" | "warning" | "info" | "success";
  title: string;
  detail: string;
};

/** Mensaje permanente: lo más crítico primero. */
export const pickPrimaryAlert = (alerts: MonitorAlert[]): MonitorAlert => {
  const order: MonitorAlert["tone"][] = ["danger", "warning", "info", "success"];
  for (const tone of order) {
    const match = alerts.find((alert) => alert.tone === tone);
    if (match) return match;
  }
  return {
    id: "all-clear",
    tone: "success",
    title: "Sin pendientes operativos",
    detail: "Las solicitudes del día tienen servicio y conductor cuando corresponde.",
  };
};

export type BuildMonitorAlertsOptions = {
  dayLabel?: string;
  /** Solo si el KPI corresponde al mismo día del filtro. */
  kpiSinServicio?: number;
};

export const buildMonitorAlerts = (
  stats: MonitorStats,
  options: BuildMonitorAlertsOptions | number = {}
): MonitorAlert[] => {
  const opts: BuildMonitorAlertsOptions =
    typeof options === "number" ? { kpiSinServicio: options } : options || {};
  const dayLabel = opts.dayLabel?.trim() || "el día filtrado";
  const alerts: MonitorAlert[] = [];
  const sinServicio = Math.max(stats.scopeSinServicio, opts.kpiSinServicio ?? 0);

  if (sinServicio > 0) {
    alerts.push({
      id: "missing-service",
      tone: "danger",
      title:
        sinServicio === 1
          ? "1 solicitud necesita servicio"
          : `${sinServicio} solicitudes necesitan servicio`,
      detail: `Día ${dayLabel}: sin cupo completo agendado a un servicio.`,
    });
  }

  if (stats.scopeSinConductor > 0) {
    const count = stats.scopeSinConductor;
    alerts.push({
      id: "missing-driver",
      tone: "warning",
      title:
        count === 1
          ? "1 servicio agendado sin conductor"
          : `${count} servicios agendados sin conductor`,
      detail: `Día ${dayLabel}: servicio asociado, falta asignar conductor.`,
    });
  }

  if (!alerts.length) {
    alerts.push({
      id: "all-clear",
      tone: "success",
      title: "Sin pendientes operativos",
      detail: `Día ${dayLabel}: solicitudes con servicio y conductor cuando corresponde.`,
    });
  }

  return alerts;
};

export type ActivityItem = {
  id: string;
  tone: "danger" | "warning" | "info" | "success";
  title: string;
  detail: string;
  when: string;
  emailId?: number;
  solicitudId?: number;
};

/** Historial persistible de notificaciones del panel. */
export type MonitorNotice = {
  id: string;
  fingerprint: string;
  tone: ActivityItem["tone"];
  title: string;
  detail: string;
  when: string;
  at: string;
  emailId?: number;
  serviceId?: number;
  solicitudId?: number;
  source?: "sync" | "alert" | "activity";
};

export const MONITOR_NOTICE_HISTORY_LIMIT = 50;

export const appendMonitorNotices = (
  history: MonitorNotice[],
  incoming: MonitorNotice[],
  limit = MONITOR_NOTICE_HISTORY_LIMIT
): MonitorNotice[] => {
  if (!incoming.length) return history;
  const known = new Set(history.map((item) => item.fingerprint));
  const fresh = incoming.filter((item) => item.fingerprint && !known.has(item.fingerprint));
  if (!fresh.length) return history;
  return [...fresh, ...history].slice(0, limit);
};

export const noticesFromAlerts = (alerts: MonitorAlert[]): MonitorNotice[] => {
  const now = new Date();
  return alerts
    .filter((alert) => alert.tone !== "success")
    .map((alert) => ({
      id: `alert-${alert.id}-${alert.title}`,
      fingerprint: `alert:${alert.id}:${alert.title}`,
      tone: alert.tone,
      title: alert.title,
      detail: alert.detail,
      when: formatRelative(now.toISOString()) || formatClock(now),
      at: now.toISOString(),
      source: "alert" as const,
    }));
};

export const noticesFromActivity = (activity: ActivityItem[]): MonitorNotice[] => {
  const nowIso = new Date().toISOString();
  return activity.map((item) => ({
    id: item.id,
    fingerprint: item.id,
    tone: item.tone,
    title: item.title,
    detail: item.detail,
    when: item.when,
    at: nowIso,
    emailId: item.emailId,
    solicitudId: item.solicitudId,
    source: "activity" as const,
  }));
};

type SyncNotificationLike = {
  id: number;
  type?: string;
  title: string;
  message: string;
  created_at: string;
  read_at?: string | null;
  payload?: {
    email_id?: number;
    service_id?: number;
    solicitud_id?: number;
  };
};

/** Notificaciones persistidas por el cron/sync (campana). */
export const noticesFromSyncNotifications = (
  items: SyncNotificationLike[]
): MonitorNotice[] =>
  items
    .filter((item) => Number.isInteger(item?.id))
    .map((item) => {
      const isUpcoming = item.type === "UPCOMING_SERVICE";
      return {
        id: `sync-${item.id}`,
        fingerprint: `sync:${item.id}`,
        tone: (isUpcoming ? "warning" : "danger") as MonitorNotice["tone"],
        title: item.title,
        detail: item.message,
        when: formatRelative(item.created_at),
        at: item.created_at,
        emailId: item.payload?.email_id,
        serviceId: item.payload?.service_id,
        solicitudId: item.payload?.solicitud_id,
        source: "sync" as const,
      };
    });

export const buildActivityFromSolicitudes = (
  data: ServiceEmailSolicitud[],
  limit = 8
): ActivityItem[] => {
  const items = [...data]
    .sort((a, b) => {
      const ta = new Date(a.updated_at || a.created_at || 0).getTime();
      const tb = new Date(b.updated_at || b.created_at || 0).getTime();
      return tb - ta;
    })
    .slice(0, limit);

  return items.map((item) => {
    const status = resolveOpsStatus(item);
    const emailId = item.email_request_id ?? item.email_request;
    const remaining = getRemaining(item);
    let tone: ActivityItem["tone"] = "info";
    let detail = `Solicitud #${item.id}`;

    if (status === "sin_servicio") {
      tone = "danger";
      detail = `Sin servicio · ${remaining} pax pendientes`;
    } else if (status === "parcial") {
      tone = "warning";
      detail = `Cobertura parcial · faltan ${remaining} pax`;
    } else if (status === "sin_conductor") {
      tone = "warning";
      detail = "Servicio agendado sin conductor";
    } else if (status === "listo") {
      tone = "success";
      detail = "Servicio y conductor listos";
    }

    const stamp = item.updated_at || item.created_at || "";
    return {
      id: `sol-${item.id}-${status}-${stamp}`,
      tone,
      title: item.passenger_names?.trim() || `Solicitud #${item.id}`,
      detail,
      when: formatRelative(stamp),
      emailId,
      solicitudId: item.id,
    };
  });
};
